import { BroadcastChannel } from 'broadcast-channel';
import startCase from 'lodash/startCase';
import throttle from 'lodash/throttle';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';

import { useConsumerStatusSummaryQuery } from 'apollo/queries/autogenerate/hooks';
import {
  useKYCOCRResultsLazyQuery,
  useRegisterLiquidApplicationInfoMutation,
  useSetPACDataMutation,
  useSetPEPStatusMutation,
} from 'apollo/queries/autogenerate/hooks';
import {
  SetPACDataMutationVariables,
  SetPEPStatusMutationVariables,
} from 'apollo/queries/autogenerate/operations';
import {
  NumberOfDependents,
  Profession,
  Purpose,
  RentOrMortgage,
} from 'apollo/queries/autogenerate/schemas';
import {
  APPLICANT_ID,
  EKYC_FORM,
  PREFECTURES,
  DEPENDENTS,
  DEPENDENTS_FORM_KEYS,
  HAVE_OR_NOT,
  HAVE_OR_NOT_FORM_KEYS,
  PEPS_STATUS,
  PROFESSIONS,
  PROFESSION_FORM_KEYS,
  PURPOSES,
  PURPOSE_FORM_KEYS,
  EKYC_APPLICATION_STATUS,
  EKYC_APPLICATION_STAGE,
  EKYC_BRODCAST_CHANNEL,
  EKYC_BRODCAST_MESSAGE,
} from 'shared/constants/ekyc';
import { isClassicUser } from 'shared/consumer/utils/consumer-types';
import Stepper from 'shared/modules/common/components/molecules/Stepper';
import useIsWebEKYCEnabled from 'shared/modules/common/hooks/use-is-web-ekyc-enabled';
import EKYCBackground from 'shared/modules/ekyc/components/atoms/Background';
import Header from 'shared/modules/ekyc/components/atoms/Header';
import Panel from 'shared/modules/ekyc/components/atoms/Panel';
import AppLoader from 'shared/modules/ekyc/components/organism/AppLoader';
import PEPInfo from 'shared/modules/ekyc/components/organism/PEPInfo';
import PEPPrecaution from 'shared/modules/ekyc/components/organism/PEPPrecaution';
import APIError from 'shared/modules/ekyc/components/organism/forms/APIError';
import AddressForm, {
  useFormAddressWithValidations,
} from 'shared/modules/ekyc/components/organism/forms/Address';
import ConfirmationForm from 'shared/modules/ekyc/components/organism/forms/Confirmation';
import PACForm, {
  useFormPACWithValidations,
} from 'shared/modules/ekyc/components/organism/forms/PAC';
import PEPForm, {
  useFormPEPWithValidations,
} from 'shared/modules/ekyc/components/organism/forms/PEP';
import PersonalInfoForm, {
  usePersonalInfoFormWithValidations,
} from 'shared/modules/ekyc/components/organism/forms/PersonalInfo';
import SuccessForm from 'shared/modules/ekyc/components/organism/forms/Success';
import { ITEM, SCREEN } from 'shared/modules/ekyc/constants/mixpanel';
import {
  mixpanelTrackClickEvent,
  mixpanelTrackAcceptParentalConsensus,
  mixpanelStartTrackPageViewDurationEvent,
  mixpanelStopTrackPageViewDurationEvent,
  mixpanelTrackFieldEditEvent,
  mixpanelTrackSelectIsPEPs,
  mixpanelTrackError,
  mixpanelTrackSuccess,
} from 'shared/modules/ekyc/utils/kyc-tracking-events';
import { createCookie, getCookie, deleteCookie } from 'shared/utils/cookie';
import { normalizeName } from 'shared/utils/normalize-user-name';
import { removeHankakuZenkakuSpaces } from 'shared/utils/normalize-user-name';
import routes from 'web/navigation/routes';

const EKYCApplyScreen = () => {
  const isBigScreen = useMediaQuery({ minWidth: 768 });
  const navigate = useNavigate();
  const [isPEPInfoShown, setIsPEPInfoShown] = React.useState(false);
  const [isPEPPrecautionShown, setPEPPrecautionShown] = React.useState(false);
  const [isSubmissionComplete, setIsSubmissionComplete] = React.useState(false);
  const [isAPIFailure, setIsAPIFailure] = React.useState(false);
  const [
    isParentalConsentChecked,
    setIsParentalConsentChecked,
  ] = React.useState(false);
  const isWebKYCEnabled = useIsWebEKYCEnabled();

  // APIs
  const { data: consumerStatus } = useConsumerStatusSummaryQuery();
  const kycStatus = consumerStatus?.KYCApplicationStatus?.status;
  const [isInitialized, setIsInitialized] = useState(false);
  const [getKycOcrResults, { data: kycData }] = useKYCOCRResultsLazyQuery();
  const [setPACDataMutation] = useSetPACDataMutation({
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });
  const [setPepStatusMutation] = useSetPEPStatusMutation({
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });
  const [
    registerLiquidApplicationInfoMutation,
  ] = useRegisterLiquidApplicationInfoMutation();

  // Forms
  const [currentForm, setCurrentForm] = useState(EKYC_FORM.PERSONAL_INFO);
  const personalInfoForm = usePersonalInfoFormWithValidations({
    lastName: '',
    firstName: '',
    lastNameKana: '',
    firstNameKana: '',
    dateOfBirth: '',
  });
  const addressForm = useFormAddressWithValidations({
    city: '',
    line1: '',
    line2: '',
    state: '',
    zip: '',
  });
  const pacForm = useFormPACWithValidations({
    profession: '',
    purpose: '',
    numberOfDependents: '',
    rentOrMortgage: '',
    salary: '',
  });
  const pepForm = useFormPEPWithValidations({
    pepStatus: '',
  });

  const applicantId = getCookie(APPLICANT_ID);
  const applicationStatus = getCookie(EKYC_APPLICATION_STATUS);

  const getOCRResultByApplicantId = async () => {
    getKycOcrResults({
      variables: {
        applicantId,
      },
    });
  };

  const trackMixpanelClick = () => {
    let item = '';

    switch (currentForm) {
      case EKYC_FORM.PERSONAL_INFO:
        item = ITEM.WEB_EKYC_PAIDY_FORM_PERSONAL;
        break;
      case EKYC_FORM.ADDRESS:
        item = ITEM.WEB_EKYC_PAIDY_FORM_ADDRESS;
        break;
      case EKYC_FORM.PAC:
        item = ITEM.WEB_EKYC_PAIDY_FORM_ADDITIONAL;
        break;
      case EKYC_FORM.PEP:
        item = ITEM.WEB_EKYC_PAIDY_FORM_PEP;
        break;
      case EKYC_FORM.CONFIRMATION:
        item = ITEM.WEB_EKYC_PAIDY_FORM_CONFIRMATION;
        break;
    }

    mixpanelTrackClickEvent(SCREEN.WEB_PAIDY_FORM, {
      Item: item,
    });
  };

  const handleOnSubmitApplication = throttle(
    async () => {
      try {
        trackMixpanelClick();
        mixpanelTrackClickEvent(SCREEN.WEB_PAIDY_FORM, {
          Item: ITEM.WEB_EKYC_SUBMIT,
        });

        // Step 1: PAC
        const salary = parseInt(pacForm.getValues().salary, 10);
        const numberOfDependentsIndex = DEPENDENTS.findIndex(
          (elem) => elem === pacForm.getValues().numberOfDependents
        );
        const rentOrMortgageIndex = HAVE_OR_NOT.findIndex(
          (elem) => elem === pacForm.getValues().rentOrMortgage
        );
        const pacVariable: SetPACDataMutationVariables = {
          salary: salary * 10000, // multiple of 万円
          numberOfDependents:
            NumberOfDependents[DEPENDENTS_FORM_KEYS[numberOfDependentsIndex]],
          rentOrMortgage:
            RentOrMortgage[HAVE_OR_NOT_FORM_KEYS[rentOrMortgageIndex]],
        };

        const pacResult = await setPACDataMutation({ variables: pacVariable });
        const pacErrors = pacResult.errors;

        if (pacErrors) {
          mixpanelTrackError(SCREEN.WEB_PAIDY_FORM, { error: pacErrors[0] });

          return setIsAPIFailure(true);
        }

        // Step 2: PEP
        const isPEP =
          pepForm.getValues().pepStatus === PEPS_STATUS[0] ? false : true;
        const pepVariable: SetPEPStatusMutationVariables = { isExposed: isPEP };

        const pepResult = await setPepStatusMutation({
          variables: pepVariable,
        });
        const pepErrors = pepResult.errors;

        if (pepErrors) {
          mixpanelTrackError(SCREEN.WEB_PAIDY_FORM, { error: pepErrors[0] });

          return setIsAPIFailure(true);
        }

        // Step 3: KYC
        const firstName = normalizeName(personalInfoForm.getValues().firstName);
        const lastName = normalizeName(personalInfoForm.getValues().lastName);
        const firstNameKana = normalizeName(
          personalInfoForm.getValues().firstNameKana
        );
        const lastNameKana = normalizeName(
          personalInfoForm.getValues().lastNameKana
        );
        const prefectureIndex = PREFECTURES.findIndex(
          (elem) => elem === addressForm.getValues().state
        );
        const professionIndex = PROFESSIONS.findIndex(
          (elem) => elem === pacForm.getValues().profession
        );
        const purposeIndex = PURPOSES.findIndex(
          (elem) => elem === pacForm.getValues().purpose
        );
        const kycVariable = {
          firstName,
          lastName,
          firstNameKana,
          lastNameKana,
          dateOfBirth: moment(personalInfoForm.getValues().dateOfBirth)
            .tz('Asia/Tokyo')
            .format('YYYY-MM-DD'),
          address: {
            line2: addressForm.getValues().line2,
            city: addressForm.getValues().city,
            state: PREFECTURES[prefectureIndex],
            zipCode: (addressForm.getValues().zip as string).replace('-', ''),
            ...(addressForm.getValues().line1 && {
              line1: addressForm.getValues().line1,
            }),
          },
          profession: Profession[PROFESSION_FORM_KEYS[professionIndex]],
          purpose: Purpose[PURPOSE_FORM_KEYS[purposeIndex]],
        };
        const kycResult = await registerLiquidApplicationInfoMutation({
          variables: { data: kycVariable },
        });

        if (kycResult && kycResult.data) {
          mixpanelTrackSuccess(SCREEN.WEB_PAIDY_FORM);
          setIsSubmissionComplete(true);

          deleteCookie(APPLICANT_ID);
          deleteCookie(EKYC_APPLICATION_STATUS);

          /**
           * Brodcast to previous opened tab to reload and navigate to account page
           */
          const bc = new BroadcastChannel(EKYC_BRODCAST_CHANNEL);

          bc.postMessage(EKYC_BRODCAST_MESSAGE.NAVIGATE_TO_ACCOUNT);
        } else if (kycResult.errors && kycResult.errors.length > 0) {
          mixpanelTrackError(SCREEN.WEB_PAIDY_FORM, {
            error: kycResult.errors[0],
          });
        }
      } catch (err) {
        /* noop */
        // TODO: DIFFERENT TICKET TO HANDLE API ERROR
        return setIsAPIFailure(true);
      }
    },
    2000,
    { leading: true, trailing: false }
  );

  const getCurrentForm = () => {
    switch (currentForm) {
      case EKYC_FORM.PERSONAL_INFO:
        return (
          <PersonalInfoForm
            form={personalInfoForm}
            isParentalConsentChecked={isParentalConsentChecked}
            onNextButtonPress={() => {
              trackMixpanelClick();
              setCurrentForm(EKYC_FORM.ADDRESS);
            }}
            onParentalConsentCheckToggle={(checked) => {
              mixpanelTrackAcceptParentalConsensus(
                SCREEN.WEB_PAIDY_FORM,
                checked
              );

              setIsParentalConsentChecked(checked);
            }}
            onFocus={() =>
              mixpanelStartTrackPageViewDurationEvent(SCREEN.WEB_PAIDY_FORM)
            }
            onBlur={() =>
              mixpanelStopTrackPageViewDurationEvent(SCREEN.WEB_PAIDY_FORM, {
                Item: ITEM.WEB_EKYC_PAIDY_FORM_PERSONAL,
              })
            }
            onFieldBlur={(field) => {
              mixpanelTrackFieldEditEvent(SCREEN.WEB_PAIDY_FORM, {
                Item: startCase(field),
              });
            }}
          />
        );
      case EKYC_FORM.ADDRESS:
        return (
          <AddressForm
            form={addressForm}
            onNextButtonPress={() => {
              trackMixpanelClick();
              setCurrentForm(EKYC_FORM.PAC);
            }}
            onReturnButtonPress={() => setCurrentForm(EKYC_FORM.PERSONAL_INFO)}
            onFocus={() =>
              mixpanelStartTrackPageViewDurationEvent(SCREEN.WEB_PAIDY_FORM)
            }
            onBlur={() =>
              mixpanelStopTrackPageViewDurationEvent(SCREEN.WEB_PAIDY_FORM, {
                Item: ITEM.WEB_EKYC_PAIDY_FORM_ADDRESS,
              })
            }
            onFieldBlur={(field) => {
              mixpanelTrackFieldEditEvent(SCREEN.WEB_PAIDY_FORM, {
                Item: startCase(field),
              });
            }}
          />
        );
      case EKYC_FORM.PAC:
        return (
          <PACForm
            form={pacForm}
            onNextButtonPress={() => {
              trackMixpanelClick();
              setCurrentForm(EKYC_FORM.PEP);
            }}
            onReturnButtonPress={() => setCurrentForm(EKYC_FORM.ADDRESS)}
            onFocus={() =>
              mixpanelStartTrackPageViewDurationEvent(SCREEN.WEB_PAIDY_FORM)
            }
            onBlur={() =>
              mixpanelStopTrackPageViewDurationEvent(SCREEN.WEB_PAIDY_FORM, {
                Item: ITEM.WEB_EKYC_PAIDY_FORM_ADDITIONAL,
              })
            }
            onFieldBlur={(field) => {
              mixpanelTrackFieldEditEvent(SCREEN.WEB_PAIDY_FORM, {
                Item: startCase(field),
              });
            }}
          />
        );
      case EKYC_FORM.PEP:
        return (
          <PEPForm
            form={pepForm}
            onNextButtonPress={() => {
              trackMixpanelClick();

              if (pepForm.getValues().pepStatus === PEPS_STATUS[1]) {
                setPEPPrecautionShown(true);
              } else {
                setCurrentForm(EKYC_FORM.CONFIRMATION);
              }
            }}
            onReturnButtonPress={() => setCurrentForm(EKYC_FORM.PAC)}
            onPEPInfoButtonPress={() => {
              mixpanelTrackClickEvent(SCREEN.WEB_PAIDY_FORM, {
                Item: ITEM.WEB_EKYC_WHAT_IS_PEP,
              });

              setIsPEPInfoShown(true);
            }}
            onFocus={() =>
              mixpanelStartTrackPageViewDurationEvent(SCREEN.WEB_PAIDY_FORM)
            }
            onBlur={() =>
              mixpanelStopTrackPageViewDurationEvent(SCREEN.WEB_PAIDY_FORM, {
                Item: ITEM.WEB_EKYC_PAIDY_FORM_PEP,
              })
            }
            onFieldBlur={(field) => {
              mixpanelTrackSelectIsPEPs(
                SCREEN.WEB_PAIDY_FORM,
                pepForm.getValues(field) === PEPS_STATUS[0] ? false : true
              );
            }}
          />
        );
      case EKYC_FORM.CONFIRMATION:
        return (
          <ConfirmationForm
            personalInfoForm={personalInfoForm}
            addressForm={addressForm}
            pacForm={pacForm}
            pepForm={pepForm}
            onNextButtonPress={handleOnSubmitApplication}
            onReturnButtonPress={() => setCurrentForm(EKYC_FORM.PERSONAL_INFO)}
            onFocus={() =>
              mixpanelStartTrackPageViewDurationEvent(SCREEN.WEB_PAIDY_FORM)
            }
            onBlur={() =>
              mixpanelStopTrackPageViewDurationEvent(SCREEN.WEB_PAIDY_FORM, {
                Item: ITEM.WEB_EKYC_PAIDY_FORM_CONFIRMATION,
              })
            }
          />
        );
      default:
        return null;
    }
  };

  const renderForm = () => {
    if (
      isPEPInfoShown ||
      isPEPPrecautionShown ||
      isSubmissionComplete ||
      isAPIFailure
    ) {
      return null;
    }

    return (
      <View style={styles.form}>
        <Text style={styles.mainTitle}>{`会員情報入力`} </Text>
        {currentForm !== EKYC_FORM.CONFIRMATION && (
          <Stepper currentStage={(currentForm as number) + 1} totalStages={4} />
        )}
        {getCurrentForm()}
      </View>
    );
  };

  const canShowBackButton = () => {
    if (isBigScreen || isSubmissionComplete || isAPIFailure) {
      return false;
    }

    if (currentForm === EKYC_FORM.PERSONAL_INFO) {
      return false;
    }

    return true;
  };

  const handleOnBackButtonPress = () => {
    if (isPEPInfoShown) {
      return setIsPEPInfoShown(false);
    } else if (isPEPPrecautionShown) {
      return setPEPPrecautionShown(false);
    } else if (isSubmissionComplete) {
      return setIsSubmissionComplete(false);
    }

    mixpanelTrackClickEvent(SCREEN.WEB_PAIDY_FORM, {
      Item: ITEM.WEB_EKYC_BACK,
    });

    switch (currentForm) {
      case EKYC_FORM.ADDRESS:
        setCurrentForm(EKYC_FORM.PERSONAL_INFO);
        break;
      case EKYC_FORM.PAC:
        setCurrentForm(EKYC_FORM.ADDRESS);
        break;
      case EKYC_FORM.PEP:
        setCurrentForm(EKYC_FORM.PAC);
        break;
      case EKYC_FORM.CONFIRMATION:
        setCurrentForm(EKYC_FORM.PEP);
        break;
    }
  };

  const handleOnOCRResults = () => {
    if (!kycData) {
      return;
    }

    const data = kycData.KYCOCRResults;

    if (data.name) {
      const names = data.name.split(' ');
      const lastName = names?.[0];
      const firstName = names?.[1];

      if (lastName) {
        personalInfoForm.setValue('lastName', lastName);
      }

      if (firstName) {
        personalInfoForm.setValue('firstName', firstName);
      }
    }

    if (data.birthday) {
      personalInfoForm.setValue('dateOfBirth', data.birthday);
    }

    if (data.zipCode) {
      const formattedZipCode = `${data.zipCode.slice(
        0,
        3
      )}-${data.zipCode.slice(3, 7)}`;

      addressForm.setValue('zipCode', formattedZipCode);
    }

    if (data.addressPref) {
      addressForm.setValue(
        'prefectureSelectedIndex',
        PREFECTURES.findIndex((x) => x === data.addressPref)
      );
    }

    if (data.addressCity) {
      addressForm.setValue('city', data.addressCity);
    }

    if (data.addressOther) {
      addressForm.setValue(
        'line2',
        removeHankakuZenkakuSpaces(data.addressOther)
      );
    }
  };

  useEffect(() => handleOnOCRResults(), [kycData]);

  useEffect(() => {
    // Wait for isWebKYCEnabled and kycStatus to be initialized
    if (isWebKYCEnabled !== null && kycStatus) {
      // Handle redirection based on this set of conditions
      if (isClassicUser(kycStatus) && isWebKYCEnabled) {
        setIsInitialized(true);
      } else {
        return navigate(routes.authenticated.home.root);
      }

      if (!applicationStatus) {
        return navigate(routes.authenticated.home.root);
      } else {
        createCookie(
          EKYC_APPLICATION_STATUS,
          EKYC_APPLICATION_STAGE.POST_APPLICATION,
          1
        );

        getOCRResultByApplicantId();
      }
    }
  }, [applicationStatus, kycStatus, isWebKYCEnabled]);

  return (
    <EKYCBackground>
      {!isInitialized ? (
        <AppLoader />
      ) : (
        <View
          style={[styles.container, isBigScreen && styles.containerBigScreen]}
        >
          <Panel style={[styles.panel, isBigScreen && styles.panelBigScreen]}>
            {!isBigScreen && (
              <Header
                showBackButton={canShowBackButton()}
                onBackButtonPress={handleOnBackButtonPress}
              />
            )}
            {isPEPInfoShown && (
              <PEPInfo onReturnButtonPress={() => setIsPEPInfoShown(false)} />
            )}
            {isPEPPrecautionShown && (
              <PEPPrecaution
                onNextButtonPress={() => {
                  setPEPPrecautionShown(false);
                  setCurrentForm(EKYC_FORM.CONFIRMATION);
                }}
                onReturnButtonPress={() => setPEPPrecautionShown(false)}
              />
            )}
            {isSubmissionComplete && <SuccessForm />}
            {isAPIFailure && (
              <APIError onReturnButtonPress={() => setIsAPIFailure(false)} />
            )}
            {renderForm()}
          </Panel>
        </View>
      )}
    </EKYCBackground>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 0,
    minHeight: '100%',
    width: '100%',
    paddingVertical: 0,
    overflow: 'hidden',
  },
  containerBigScreen: {
    flex: 1,
    position: 'relative',
    alignItems: 'center',
    paddingBottom: 30,
    paddingTop: 80,
    minHeight: 'auto',
    overflow: 'scroll',
  },
  form: {
    flexDirection: 'column',
    flexGrow: 1,
  },
  mainTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#000',
  },
  panel: {
    maxWidth: 424,
    minHeight: '100svh',
    paddingBottom: 18,
    paddingLeft: 16,
    paddingRight: 15,
  },
  panelBigScreen: {
    paddingTop: 60,
    paddingBottom: 60,
    paddingLeft: 24,
    paddingRight: 24,
    minHeight: 'auto',
  },
});

export default EKYCApplyScreen;
